import React, { useState } from 'react';
import { speakTextWithFallback } from '../utils/voiceManager';

const GrammarAnalysisDisplay = ({ sentence, analysis, translation, displayLanguage }) => {
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showTranslation, setShowTranslation] = useState(false);
  const [drillExamples, setDrillExamples] = useState([]);
  const [isDrillLoading, setIsDrillLoading] = useState(false);
  const [drillStatus, setDrillStatus] = useState(null);
  const [currentDrillIndex, setCurrentDrillIndex] = useState(0);
  
  // Pronunciation and recording states
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingText, setPlayingText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  const getText = (textObj) => {
    if (!textObj) return '';
    if (typeof textObj === 'string') return textObj;
    
    const languageMap = {
      'traditional': 'tc',
      'simplified': 'sc',
      'english': 'en'
    };
    
    const key = languageMap[displayLanguage] || displayLanguage;
    return textObj[key] || textObj.tc || textObj.en || '';
  };

  // DeepSeek API configuration
  const DEEPSEEK_API_KEY = 'sk-7549b29d010a424a836d081a478552f4';
  const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

  // Calculate cost based on DeepSeek API tariff
  const calculateCost = (inputTokens, outputTokens, isCacheHit = false) => {
    const now = new Date();
    const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));
    const hour = beijingTime.getUTCHours();
    const isDiscountTime = hour >= 0 && hour < 8;
    
    let inputCost, outputCost;
    
    if (isDiscountTime) {
      inputCost = isCacheHit ? 0.25 : 1.0;
      outputCost = 4.0;
    } else {
      inputCost = isCacheHit ? 0.5 : 2.0;
      outputCost = 8.0;
    }
    
    const totalInputCost = (inputTokens / 1000000) * inputCost;
    const totalOutputCost = (outputTokens / 1000000) * outputCost;
    
    return {
      inputCost: totalInputCost,
      outputCost: totalOutputCost,
      totalCost: totalInputCost + totalOutputCost,
      isDiscountTime
    };
  };

  // Generate AI Drill examples
  const generateDrillExamples = async () => {
    setIsDrillLoading(true);
    setDrillStatus({
      timeElapsed: 0,
      tokensUsed: 0,
      costIncurred: 0,
      isDiscountTime: false
    });
    
    const startTime = Date.now();
    
    try {
      const grammarPattern = analysis.match(/\(([SVOCM][^)]*)\)/g)?.map(tag => tag.slice(1, -1)) || [];
      const patternString = grammarPattern.join('+');
      
      const prompt = `Generate 3 similar English grammar examples for practice. 

Original example:
Sentence: "${sentence}"
Grammar analysis: "${analysis}"
Grammar pattern: ${patternString}

Requirements:
1. Create 3 new sentences with the SAME grammar pattern: ${patternString}
2. Use similar difficulty level and word tone
3. Maintain the same grammar category (Present Simple)
4. Each sentence should be natural and educational
5. Provide grammar analysis for each sentence in the format: "word (S) word (V) word (O)" etc.

CRITICAL: Return ONLY valid JSON array. Do not include any explanations, comments, or additional text before or after the JSON.

Return the response as a JSON array with this exact format:
[
  {
    "sentence": "example sentence 1",
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation", 
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 2", 
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 3",
    "analysis": "word (S) word (V) word (O)", 
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  }
]`;

      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [{ role: 'user', content: prompt }],
          temperature: 0.7,
          max_tokens: 1000
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(`API Error: ${data.error?.message || 'Unknown error'}`);
      }

      const content = data.choices[0].message.content;
      
      // Clean and parse the JSON response
      let drillData;
      try {
        // Check if content is empty or null
        if (!content || content.trim() === '') {
          throw new Error('Empty response from API');
        }
        
        // Try to extract JSON from the response (in case there's extra text)
        const jsonMatch = content.match(/\[[\s\S]*\]/);
        if (jsonMatch) {
          drillData = JSON.parse(jsonMatch[0]);
        } else {
          // If no array found, try parsing the entire content
          drillData = JSON.parse(content);
        }
      } catch (parseError) {
        console.error('JSON parsing failed:', parseError);
        console.log('Raw content:', content);
        
        // Fallback: Create mock drill data based on the original example
        drillData = [
          {
            sentence: `${sentence} (Drill 1)`,
            analysis: analysis,
            translation: {
              tc: `${getText(translation)} (練習1)`,
              sc: `${getText(translation)} (练习1)`,
              en: `${sentence} (Drill 1)`
            }
          },
          {
            sentence: `${sentence} (Drill 2)`,
            analysis: analysis,
            translation: {
              tc: `${getText(translation)} (練習2)`,
              sc: `${getText(translation)} (练习2)`,
              en: `${sentence} (Drill 2)`
            }
          },
          {
            sentence: `${sentence} (Drill 3)`,
            analysis: analysis,
            translation: {
              tc: `${getText(translation)} (練習3)`,
              sc: `${getText(translation)} (练习3)`,
              en: `${sentence} (Drill 3)`
            }
          }
        ];
      }
      
      // Validate drill data structure
      if (!Array.isArray(drillData) || drillData.length === 0) {
        throw new Error('Invalid drill data structure: Expected non-empty array');
      }
      
      // Ensure each drill example has the required fields
      drillData = drillData.map((item, index) => {
        if (!item.sentence || !item.analysis || !item.translation) {
          // Create fallback item if structure is invalid
          return {
            sentence: `${sentence} (Drill ${index + 1})`,
            analysis: analysis,
            translation: {
              tc: `${getText(translation)} (練習${index + 1})`,
              sc: `${getText(translation)} (练习${index + 1})`,
              en: `${sentence} (Drill ${index + 1})`
            }
          };
        }
        return item;
      });
      
      const timeElapsed = (Date.now() - startTime) / 1000;
      const inputTokens = data.usage.prompt_tokens;
      const outputTokens = data.usage.completion_tokens;
      const costInfo = calculateCost(inputTokens, outputTokens);
      
      setDrillExamples(drillData);
      setDrillStatus({
        timeElapsed: timeElapsed.toFixed(2),
        tokensUsed: inputTokens + outputTokens,
        costIncurred: costInfo.totalCost.toFixed(4),
        isDiscountTime: costInfo.isDiscountTime
      });
      
    } catch (error) {
      console.error('AI Drill generation failed:', error);
      setDrillStatus({
        error: error.message,
        timeElapsed: ((Date.now() - startTime) / 1000).toFixed(2)
      });
    } finally {
      setIsDrillLoading(false);
    }
  };

  // Parse the analysis string to extract components
  const parseAnalysis = (analysisStr) => {
    const components = [];
    const regex = /([^()]+?)\s*\(([SVOCM][^)]*)\)/g;
    let match;
    
    while ((match = regex.exec(analysisStr)) !== null) {
      components.push({
        text: match[1].trim(),
        role: match[2].trim()
      });
    }
    
    return components;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      'S': 'bg-blue-500',
      'V': 'bg-green-500',
      'O': 'bg-yellow-500',
      'C': 'bg-purple-500',
      'M': 'bg-red-500'
    };
    return colorMap[role.charAt(0)] || 'bg-gray-500';
  };

  // Enhanced Text-to-Speech function with mobile Safari optimization
  const speakText = (text) => {
    speakTextWithFallback(
      text,
      null, // availableVoices will be loaded by the voice manager
      (playingText) => {
        setIsPlaying(true);
        setPlayingText(playingText);
      },
      () => {
        setIsPlaying(false);
        setPlayingText('');
      },
      (event) => {
        setIsPlaying(false);
        setPlayingText('');
        if (event && event.error === 'not-allowed') {
          alert(getText({ tc: '請允許語音權限', sc: '请允许语音权限', en: 'Please allow speech permission' }));
        }
      }
    );
  };

  // Enhanced recording functions with better audio quality and format
  const startRecording = async () => {
    try {
      // Request high-quality audio
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 44100,
          channelCount: 1
        } 
      });
      
      // Use better audio format for compatibility
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/mp4';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setRecordingUrl(url);
        setIsRecording(false);
        
        // Clean up the stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.onerror = (event) => {
        console.error('Recording error:', event);
        setIsRecording(false);
        alert(getText({ tc: '錄音失敗', sc: '录音失败', en: 'Recording failed' }));
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    } catch (error) {
      console.error('Error starting recording:', error);
      if (error.name === 'NotAllowedError') {
        alert(getText({ tc: '請允許麥克風權限', sc: '请允许麦克风权限', en: 'Please allow microphone permission' }));
      } else if (error.name === 'NotFoundError') {
        alert(getText({ tc: '找不到麥克風設備', sc: '找不到麦克风设备', en: 'No microphone found' }));
      } else {
        alert(getText({ tc: '錄音啟動失敗', sc: '录音启动失败', en: 'Failed to start recording' }));
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping recording:', error);
        setIsRecording(false);
      }
    }
  };

  const playRecording = () => {
    if (recordingUrl) {
      try {
        const audio = new Audio(recordingUrl);
        
        // Add event listeners for better playback control
        audio.onloadstart = () => {
          console.log('Audio loading started');
        };
        
        audio.oncanplay = () => {
          console.log('Audio can play');
        };
        
        audio.onplay = () => {
          console.log('Audio playback started');
        };
        
        audio.onended = () => {
          console.log('Audio playback ended');
        };
        
        audio.onerror = (event) => {
          console.error('Audio playback error:', event);
          alert(getText({ tc: '音頻播放失敗', sc: '音频播放失败', en: 'Audio playback failed' }));
        };
        
        // Set audio properties for better playback
        audio.volume = 1.0;
        audio.playbackRate = 1.0;
        
        const playPromise = audio.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.error('Audio play failed:', error);
            alert(getText({ tc: '音頻播放失敗', sc: '音频播放失败', en: 'Audio playback failed' }));
          });
        }
      } catch (error) {
        console.error('Error playing recording:', error);
        alert(getText({ tc: '音頻播放失敗', sc: '音频播放失败', en: 'Audio playback failed' }));
      }
    }
  };

  const clearRecording = () => {
    if (recordingUrl) {
      URL.revokeObjectURL(recordingUrl);
      setRecordingUrl(null);
    }
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      try {
        mediaRecorder.stop();
      } catch (error) {
        console.error('Error stopping media recorder:', error);
      }
    }
    setIsRecording(false);
  };

  const components = parseAnalysis(analysis);

  return (
    <div className="space-y-4">
      {/* Original Sentence with AI Drill Button */}
      <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600">
        <h4 className="text-sm text-gray-400 mb-2">
          {getText({ tc: '例句', sc: '例句', en: 'Example Sentence' })}
        </h4>
        <div className="flex items-center justify-between mb-3">
          <p className="text-lg text-white">{sentence}</p>
          <button
            onClick={() => speakText(sentence)}
            className={`p-2 rounded-lg transition-all ${
              isPlaying && playingText === sentence
                ? 'bg-orange-600 text-white' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
            title={getText({ tc: '發音', sc: '发音', en: 'Pronounce' })}
          >
            <span className="text-lg">{isPlaying && playingText === sentence ? '⏸️' : '🔊'}</span>
          </button>
        </div>
        
        {/* AI Drill Button */}
        <div className="flex items-center justify-between">
          <button
            onClick={generateDrillExamples}
            disabled={isDrillLoading}
            className={`px-4 py-2 rounded-lg font-semibold transition-all ${
              isDrillLoading 
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                : 'bg-purple-600 hover:bg-purple-700 text-white'
            }`}
          >
            {isDrillLoading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{getText({ tc: '生成中...', sc: '生成中...', en: 'Generating...' })}</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span>🤖</span>
                <span>{getText({ tc: 'AI 練習', sc: 'AI 练习', en: 'AI Drill' })}</span>
              </div>
            )}
          </button>
          
          {/* Drill Status */}
          {drillStatus && (
            <div className="text-xs text-gray-400 space-y-1">
              <div>⏱️ {getText({ tc: '耗時', sc: '耗时', en: 'Time' })}: {drillStatus.timeElapsed}s</div>
              <div>🔢 {getText({ tc: '詞數', sc: '词数', en: 'Tokens' })}: {drillStatus.tokensUsed}</div>
              <div>💰 {getText({ tc: '費用', sc: '费用', en: 'Cost' })}: ¥{drillStatus.costIncurred}</div>
              {drillStatus.isDiscountTime && (
                <div className="text-green-400">🎉 {getText({ tc: '優惠時段', sc: '优惠时段', en: 'Discount Time' })}</div>
              )}
            </div>
          )}
        </div>
        
        {/* Error Display */}
        {drillStatus?.error && (
          <div className="mt-3 p-3 bg-red-900/30 rounded-lg border border-red-600">
            <p className="text-red-300 text-sm">
              {getText({ tc: '錯誤', sc: '错误', en: 'Error' })}: {drillStatus.error}
            </p>
            <p className="text-red-200 text-xs mt-1">
              {getText({ 
                tc: '已生成備用練習例句', 
                sc: '已生成备用练习例句', 
                en: 'Fallback drill examples have been generated' 
              })}
            </p>
          </div>
        )}
      </div>

      {/* AI Drill Examples */}
      {drillExamples.length > 0 && (
        <div className="p-4 bg-purple-900/30 rounded-lg border border-purple-600">
          <h4 className="text-sm text-purple-300 mb-3">
            {getText({ tc: 'AI 練習例句', sc: 'AI 练习例句', en: 'AI Drill Examples' })}
          </h4>
          
          {/* Drill Navigation */}
          <div className="flex justify-center space-x-2 mb-4">
            {drillExamples.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentDrillIndex(index)}
                className={`w-10 h-10 rounded-full border-2 transition-all ${
                  currentDrillIndex === index
                    ? 'bg-purple-600 border-purple-400 text-white'
                    : 'bg-gray-700 border-gray-500 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {index + 1}
              </button>
            ))}
          </div>
          
          {/* Current Drill Example */}
          <div className="space-y-3">
            <div className="p-3 bg-gray-800/50 rounded-lg">
              <h5 className="text-sm text-gray-400 mb-2">
                {getText({ tc: '練習例句', sc: '练习例句', en: 'Drill Example' })} {currentDrillIndex + 1}
              </h5>
              <p className="text-lg text-white mb-2">{drillExamples[currentDrillIndex].sentence}</p>
              
              {/* Pronunciation and Recording Controls */}
              <div className="flex items-center space-x-3 mb-3">
                {/* Pronunciation Button */}
                <button
                  onClick={() => speakText(drillExamples[currentDrillIndex].sentence)}
                  className={`p-2 rounded-lg transition-all ${
                    isPlaying && playingText === drillExamples[currentDrillIndex].sentence
                      ? 'bg-orange-600 text-white' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white'
                  }`}
                  title={getText({ tc: '發音', sc: '发音', en: 'Pronounce' })}
                >
                  <span className="text-lg">{isPlaying && playingText === drillExamples[currentDrillIndex].sentence ? '⏸️' : '🔊'}</span>
                </button>
                
                {/* Recording Controls */}
                <div className="flex items-center space-x-2">
                  {!isRecording && !recordingUrl && (
                    <button
                      onClick={startRecording}
                      className="p-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-all"
                      title={getText({ tc: '錄音', sc: '录音', en: 'Record' })}
                    >
                      <span className="text-lg">🎤</span>
                    </button>
                  )}
                  
                  {isRecording && (
                    <button
                      onClick={stopRecording}
                      className="p-2 rounded-lg bg-red-700 text-white transition-all animate-pulse"
                      title={getText({ tc: '停止', sc: '停止', en: 'Stop' })}
                    >
                      <span className="text-lg">⏹️</span>
                    </button>
                  )}
                  
                  {recordingUrl && (
                    <>
                      <button
                        onClick={playRecording}
                        className="p-2 rounded-lg bg-green-600 hover:bg-green-700 text-white transition-all"
                        title={getText({ tc: '播放', sc: '播放', en: 'Play' })}
                      >
                        <span className="text-lg">▶️</span>
                      </button>
                      
                      <button
                        onClick={clearRecording}
                        className="p-2 rounded-lg bg-gray-600 hover:bg-gray-700 text-white transition-all"
                        title={getText({ tc: '清除', sc: '清除', en: 'Clear' })}
                      >
                        <span className="text-lg">🗑️</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Drill Analysis Display */}
              <div className="p-3 bg-blue-900/30 rounded-lg border border-blue-600">
                <h6 className="text-sm text-blue-300 mb-2">
                  {getText({ tc: '語法分析', sc: '语法分析', en: 'Grammar Analysis' })}
                </h6>
                <div className="flex flex-wrap gap-2">
                  {parseAnalysis(drillExamples[currentDrillIndex].analysis).map((component, idx) => (
                    <span
                      key={idx}
                      className={`px-2 py-1 rounded text-xs font-mono ${getRoleColor(component.role)}`}
                    >
                      {component.text} ({component.role})
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Drill Translation */}
              <div className="p-3 bg-green-900/30 rounded-lg border border-green-600">
                <h6 className="text-sm text-green-300 mb-2">
                  {getText({ tc: '翻譯', sc: '翻译', en: 'Translation' })}
                </h6>
                <p className="text-green-200">{getText(drillExamples[currentDrillIndex].translation)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Original Grammar Analysis */}
      <div className="p-4 bg-blue-900/30 rounded-lg border border-blue-600">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm text-blue-300">
            {getText({ tc: '語法分析', sc: '语法分析', en: 'Grammar Analysis' })}
          </h4>
          <button
            onClick={() => setShowAnalysis(!showAnalysis)}
            className="text-blue-300 hover:text-blue-200"
          >
            {showAnalysis ? '隱藏' : '顯示'}
          </button>
        </div>
        {showAnalysis && (
          <div className="space-y-3">
            <div className="p-3 bg-gray-800/50 rounded-lg">
              <h5 className="text-sm text-gray-400 mb-2">
                {getText({ tc: '主句模式', sc: '主句模式', en: 'Main Clause Pattern' })}
              </h5>
              <div className="flex flex-wrap gap-2">
                {components.map((component, idx) => (
                  <span
                    key={idx}
                    className={`px-2 py-1 rounded text-xs font-mono ${getRoleColor(component.role)}`}
                  >
                    {component.text} ({component.role})
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Translation */}
      <div className="p-4 bg-green-900/30 rounded-lg border border-green-600">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm text-green-300">
            {getText({ tc: '翻譯', sc: '翻译', en: 'Translation' })}
          </h4>
          <button
            onClick={() => setShowTranslation(!showTranslation)}
            className="text-green-300 hover:text-green-200"
          >
            {showTranslation ? '隱藏' : '顯示'}
          </button>
        </div>
        {showTranslation && (
          <div className="space-y-2">
            <p className="text-green-200 text-lg">{getText(translation)}</p>
            {displayLanguage !== 'en' && (
              <p className="text-gray-400 text-sm italic">
                {getText({ tc: '原文', sc: '原文', en: 'Original' })}: {translation?.en || sentence}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrammarAnalysisDisplay;
